import { Col, Row, Card, Alert, Form, Button, Table } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import Pagination from './../../../../components/shared/Pagination';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import { VendorToCompanyID } from '../../../../components/shared/Company';
import { usePreviousValue } from '../../../../hooks/usePreviousValue';
import { LoadReceipt, LoadSearchedReceipts } from '../../../../features/Receipt/ReceiptSlice';

function PurchaseOrderReceipts() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentVendor } = useSelector((state) => state.vendor);
  const previousVendorValue = usePreviousValue(currentVendor.vendNo);
  const loading = useSelector((state) => state.loader.open);
  const receipts = useSelector((state) => state.receipt.list);
  const { count, fetchingReceipt } = useSelector((state) => state.receipt);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [filterReceipts, setFilterReceipts] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [selectedFields] = useState(['number', 'orderNumber', 'postingDate', 'vendorName']);
  const [currentPage, setCurrentPage] = useState(() => {
    if (searchParams.get('currentPage')) {
      return Number(searchParams.get('currentPage'));
    } else {
      return 1;
    }
  });

  const [skipPaginate, setSkipPaginate] = useState(() => {
    if (searchParams.get('skipPaginate') && searchParams.get('skipPaginate') === 'true') {
      return true;
    }
    return false;
  });

  useEffect(() => {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (searchParams.get('pageSize')) {
      setPageSize(Number(searchParams.get('pageSize')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize: 50 })}`);
    }
    if (searchParams.get('currentPage')) {
      setCurrentPage(Number(searchParams.get('currentPage')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, currentPage })}`);
    }
  }, []);

  async function updateParams() {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    queryParams.skipPaginate = skipPaginate;
    let page;
    if (isFirstLoad) {
      if (Number(searchParams.get('currentPage')) > currentPage) {
        page = Number(searchParams.get('currentPage'));
      } else {
        page = currentPage;
      }
    } else {
      page = currentPage;
    }
    setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize, currentPage: page })}`);
  }

  async function fetchReceiptList() {
    await dispatch(loaderActions.show());
    const filter = [`vendorNumber eq '${currentVendor.vendNo}'`];

    if (currentVendor && !fetchingReceipt) {
      await dispatch(
        LoadReceipt({
          companyId: VendorToCompanyID(currentVendor),
          filter: filter.join(' and '),
          type: 'list',
          select: selectedFields.join(','),
          currentPage,
          pageSize,
          skipPaginate
        })
      );
    }
    await dispatch(loaderActions.hide());
  }

  async function loadFilteredReceipts() {
    const filter = [`vendorNumber eq '${currentVendor.vendNo}'`];
    filter.push(`contains(orderNumber,'${search}')`);
    await dispatch(loaderActions.show());
    await dispatch(
      LoadSearchedReceipts({
        companyId: VendorToCompanyID(currentVendor),
        filter: filter.join(' and '),
        select: selectedFields.join(',')
      })
    );
    await dispatch(loaderActions.hide());
  }

  useEffect(() => {
    updateParams();
    fetchReceiptList();
    if (previousVendorValue !== undefined && Object.keys(currentVendor).length > 0) {
      if (previousVendorValue !== currentVendor.vendNo) {
        setCurrentPage(1);
      }
    }
  }, [currentVendor, pageSize, currentPage, skipPaginate]);

  const ReceiptList = filterReceipts.map((receipt, index) => {
    return (
      <tr key={index}>
        <td>
          <Link to={`/purchase-receipt/${currentVendor.VendorType}/${receipt.number}`}>
            {receipt.number}
          </Link>
        </td>
        <td>{receipt.orderNumber}</td>
        <td>{receipt.postingDate}</td>
        <td>{receipt.vendorName}</td>
      </tr>
    );
  });

  const ErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Purchase orders receipts for ${currentVendor.vendName}`
        ) : (
          <> No Purchase orders receipts</>
        )}
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <>No Purchase order receipts matching current search</>
      </Alert>
    </>
  );

  useEffect(() => {
    const filterReceipts = receipts.filter((receipt) =>
      receipt.orderNumber.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilterReceipts(filterReceipts);
  }, [receipts]);

  useEffect(() => {
    const filterReceipts = receipts.filter((receipt) =>
      receipt.orderNumber.toLowerCase().includes(search.trim().toLowerCase())
    );
    setFilterReceipts(filterReceipts);
    if (filterReceipts.length === 0 && search.trim() !== '') {
      loadFilteredReceipts();
    }
  }, [search]);

  return (
    <>
      <Row className="justify-content-space-between">
        <Col md={{ span: 4 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <Button variant="link" onClick={() => setSkipPaginate(!skipPaginate)}>
            {skipPaginate === true ? 'Apply Pagination' : 'Show All'}
          </Button>
        </Col>
        <Col md={{ span: 4, offset: 4 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <div className="d-flex justify-content-end">
            <Form.Control
              size="sm"
              type="text"
              className="mr-2"
              placeholder="Search PO Receipt by #"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xl={12} md={12}>
          {filterReceipts.length > 0 ? (
            <Card className="shadow-sm pt-2">
              <Card.Body>
                <Table striped bordered hover responsive="sm">
                  <thead>
                    <tr>
                      <th>Purchase Receipt #</th>
                      <th>Purchase Order #</th>
                      <th>Posting Date</th>
                      <th>Vendor Name</th>
                    </tr>
                  </thead>
                  <tbody>{ReceiptList}</tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : receipts.length > 0 ? (
            SearchErrorCard
          ) : loading ? (
            <></>
          ) : (
            ErrorCard
          )}
          {skipPaginate === false && count > pageSize ? (
            search.trim().length === 0 ? (
              <Card className="shadow-sm pt-2">
                <Card.Body>
                  <Row>
                    <Col>
                      <p>Total Orders: {count}</p>
                      <p>
                        Currently Viewing : {(currentPage - 1) * pageSize + 1} to{' '}
                        {currentPage * pageSize > count ? count : currentPage * pageSize}
                      </p>
                    </Col>
                    <Col className="text-center">
                      <Form.Group className="mt-2">
                        <Form.Control
                          as="select"
                          onChange={(e) => {
                            setCurrentPage(1);
                            setPageSize(e.target.value);
                          }}
                        >
                          <option value="10">Orders per page: 10</option>
                          <option value="20">Orders per page: 20</option>
                          <option value="25">Orders per page: 25</option>
                          <option value="50">Orders per page: 50</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col className="text-right mt-2">
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={count}
                        pageSize={pageSize}
                        onPageChange={(page) => {
                          setIsFirstLoad(false);
                          setCurrentPage(page);
                        }}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            )
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </>
  );
}

export default PurchaseOrderReceipts;
